// Post-context tokens here must match server/Core/Pluspoint.Infrastructure/Tools/PostMessageTools.cs;
// the SUPPORTED_POST_PLACEHOLDER_TOKENS contract test (and its NUnit counterpart) enforces this.
// Inbox-context tokens here must match server/Modules/Pluspoint.Modules.Inbox/InboxPlaceholders.cs;
// the SUPPORTED_INBOX_PLACEHOLDER_TOKENS contract test (and its NUnit counterpart) enforces this.

export const PLACEHOLDER_CONTEXT = {
    INBOX: 'inbox',
    POST: 'post',
    SURVEY_TEMPLATE: 'survey_template',
    SEND_SURVEY: 'send_survey',
    CAMPAIGN: 'campaign',
    REPLY_TEMPLATE: 'reply_template',
    REVIEW_REPLY: 'review_reply',
};

const { INBOX, POST, SURVEY_TEMPLATE, SEND_SURVEY, CAMPAIGN, REPLY_TEMPLATE, REVIEW_REPLY } = PLACEHOLDER_CONTEXT;

export const RECOVERY_FORM_TOKEN = 'recovery_form';

export const PLACEHOLDERS = [
    {
        token: 'name',
        labelKey: 'common.firstName',
        contexts: [INBOX, SURVEY_TEMPLATE, SEND_SURVEY, CAMPAIGN, REPLY_TEMPLATE],
    },
    { token: 'last_name', labelKey: 'common.lastName', contexts: [INBOX, SURVEY_TEMPLATE, SEND_SURVEY, CAMPAIGN] },
    {
        token: 'location',
        labelKey: 'SettingsCompanies.locationName',
        contexts: [INBOX, POST, SURVEY_TEMPLATE, SEND_SURVEY, CAMPAIGN, REPLY_TEMPLATE],
    },
    { token: 'brand', labelKey: 'common.brand', contexts: [INBOX, POST, CAMPAIGN] },
    {
        token: 'geomodifier',
        labelKey: 'SettingsCompanies.geoModifier',
        contexts: [INBOX, POST, CAMPAIGN],
        requiresLocation: true,
    },
    {
        token: 'primary_phone',
        labelKey: 'SettingsCompanies.primaryPhone',
        contexts: [INBOX, POST, CAMPAIGN],
        requiresLocation: true,
    },
    { token: 'email', labelKey: 'common.email', contexts: [INBOX, POST, CAMPAIGN], requiresLocation: true },
    {
        token: 'website',
        labelKey: 'SettingsCompanies.website',
        contexts: [INBOX, POST, CAMPAIGN],
        requiresLocation: true,
    },
    {
        token: 'address_line_1',
        labelKey: 'SettingsCompanies.addressLine1',
        contexts: [INBOX, POST, CAMPAIGN],
        requiresLocation: true,
    },
    {
        token: 'address_line_2',
        labelKey: 'SettingsCompanies.addressLine2',
        contexts: [INBOX, POST, CAMPAIGN],
        requiresLocation: true,
    },
    { token: 'city', labelKey: 'SettingsCompanies.city', contexts: [INBOX, POST, CAMPAIGN], requiresLocation: true },
    { token: 'state', labelKey: 'SettingsCompanies.state', contexts: [INBOX, POST, CAMPAIGN], requiresLocation: true },
    {
        token: 'postal_code',
        labelKey: 'SettingsCompanies.postalCode',
        contexts: [INBOX, POST, CAMPAIGN],
        requiresLocation: true,
    },
    {
        token: 'country',
        labelKey: 'SettingsCompanies.country',
        contexts: [INBOX, POST, CAMPAIGN],
        requiresLocation: true,
    },
    {
        token: RECOVERY_FORM_TOKEN,
        labelKey: 'common.recoveryForm',
        contexts: [REPLY_TEMPLATE, REVIEW_REPLY],
        requiresSurveys: true,
    },
    { token: 'survey', labelKey: 'common.survey', contexts: [INBOX], hasRefId: true },
    { token: 'coupon', labelKey: 'common.coupon', contexts: [INBOX], hasRefId: true },
];

const tokenIndex = PLACEHOLDERS.reduce((acc, p) => {
    acc[p.token] = p;
    return acc;
}, {});

export const getPlaceholdersForContext = ctx => PLACEHOLDERS.filter(p => p.contexts.includes(ctx));

export const getPlaceholderByToken = token => tokenIndex[token] || null;

export const getPlaceholderTokensForContext = ctx => getPlaceholdersForContext(ctx).map(p => p.token);

export const SUPPORTED_POST_PLACEHOLDER_TOKENS = getPlaceholdersForContext(POST).map(p => `[${p.token}]`);

// refId tokens surface as their `[token:` prefix — the emitted form is `[token:UUID]`.
export const SUPPORTED_INBOX_PLACEHOLDER_TOKENS = getPlaceholdersForContext(INBOX).map(p =>
    p.hasRefId ? `[${p.token}:` : `[${p.token}]`,
);

// Token set TextEditor uses when callers don't opt into a specific context, and that
// testEditorLength callers should pass to scope length discounting consistently.
export const DEFAULT_PLACEHOLDER_TOKENS = ['name', 'last_name', 'location'];

// Campaign-context tokens must match server/.../Features/Campaigns/Services/CampaignPlaceholderResolver.cs;
// the campaignPlaceholders contract test (and its NUnit counterpart) enforces this.
export const CAMPAIGN_PLACEHOLDER_TOKENS = getPlaceholderTokensForContext(CAMPAIGN);

export const INBOX_PLACEHOLDER_TOKENS = getPlaceholderTokensForContext(INBOX);

export const REPLY_TEMPLATE_PLACEHOLDER_TOKENS = getPlaceholderTokensForContext(REPLY_TEMPLATE);

export const createPlaceholderResolver = (t, context) => {
    const placeholders = context ? getPlaceholdersForContext(context) : PLACEHOLDERS;
    return {
        tokens: placeholders.map(p => p.token),
        getLabel: token => {
            const def = getPlaceholderByToken(token);
            return def ? t(def.labelKey) : token;
        },
    };
};
